import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import type { BrandAlphabetAdapters } from '../../../module/brand/brandAdapters';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { StripJsonLd } from '@vs-shop/schemas/transform/withoutJsonLd';
import { matchStatus } from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/brands/alphabet';
const HTTP_METHOD = 'get';

export type MakeListBrandsAlphabetDeps<BrandAlphabet extends Schema.Top> = {
  readonly brandAlphabet: BrandAlphabet;
  readonly brandAlphabetAdapters: BrandAlphabetAdapters<StripJsonLd<BrandAlphabet['Type']>>;
};

export const makeListBrandsAlphabet = <BrandAlphabet extends Schema.Top>(
  deps: MakeListBrandsAlphabetDeps<BrandAlphabet>
) => {
  const ListBrandsAlphabetError = createBackendApiError({
    domain: 'Brand',
    endpoint: 'ListAlphabet',
    path: ENDPOINT,
    identifierField: 'page',
  });

  const listBrandsAlphabetFetch = Effect.fn(`http.${HTTP_METHOD} ${ENDPOINT}`, {
    kind: 'client',
    attributes: {
      'http.request.method': HTTP_METHOD,
    },
  })(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/ld+json')
      .fetch({});
  });

  const listBrandsAlphabet = () =>
    listBrandsAlphabetFetch().pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.brandAlphabet)(response),
          404: (response) =>
            Effect.fail(new ListBrandsAlphabetError.NotFoundError({ page: 1, response })),
          orElse: (response) =>
            Effect.fail(new ListBrandsAlphabetError.ApiRequestError({ page: 1, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.BrandListAlphabet,
          'Debug',
          `Successfully resolved alphabet with ${String(deps.brandAlphabetAdapters.letters(result).length)} letters`
        )
      ),
      Effect.withSpan('api.brand.listAlphabet', {
        kind: 'internal',
      })
    );

  return { listBrandsAlphabet, ListBrandsAlphabetError };
};
